import {
    i18nMoney,
    i18nMoment,
} from 'wanderu-i18n';
import {
    regionFromStateAndCountry,
} from 'wanderu-utils';

const LAYOVER_TO_STOP_THRESHOLD = 900; // seconds

function arrivesNextDay(departDateTime, arriveDateTime) {
    const arriveMoment = i18nMoment(arriveDateTime, 'YYYY-MM-DD');
    const departMoment = i18nMoment(departDateTime, 'YYYY-MM-DD');

    return arriveMoment.dayOfYear() > departMoment.dayOfYear()
        || arriveMoment.year() > departMoment.year();
}

function getNumberOfMidnightsPassed(departDateTime, arriveDateTime) {
    const arriveMoment = i18nMoment(arriveDateTime, 'YYYY-MM-DD');
    const departMoment = i18nMoment(departDateTime, 'YYYY-MM-DD');
    return arriveMoment.diff(departMoment, 'days');
}

/**
 * map data into a data structure representing a stop
 * TODO: this should somehow provide extra information when this is the first stop
 * after a country border has been crossed
 * @param {Object} rawData
 * @param {String} which - <'arrive'|'depart'>
 * @return {Object} an object described by `departArriveSchema`
 */
function buildEndpointData(rawData, which) {
    const {
        [`${which}_cityname`]: cityName,
        [`${which}_country`]: country,
        [`${which}_datetime`]: rawDateTime,
        [`${which}_datetime_utc`]: rawDateTimeUTC, // unix time: seconds since epoch
        [`${which}_name`]: name,
        [`${which}_state`]: state,
        [`${which}_timezone`]: rawTimezone,
        [`${which}_timezone_offset`]: rawTimezoneOffset,
        [`${which}_id`]: stationId,
        [`${which}_wcityid`]: wcityid,
    } = rawData;
    const region = regionFromStateAndCountry(state, country);

    const localizedDateTime = buildLocalizedTime({
        dateTime: rawDateTime,
        dateTimeUTC: rawDateTimeUTC,
        timezone: rawTimezone,
        timezoneOffset: rawTimezoneOffset,
    });

    return {
        cityName,
        localizedDateTime,
        name,
        region,
        stationId,
        wcityid,
    };
}

function buildLocalizedTime(params = {}) {
    const {
        dateTime,
        dateTimeUTC,
        timezone,
        timezoneOffset,
    } = params;

    if (!timezone && !dateTimeUTC) {
        return undefined;
    }

    if (!timezone) {
        return i18nMoment.utc(dateTimeUTC * 1000).utcOffset(timezoneOffset).format();
    }

    return i18nMoment.utc(dateTime * 1000).tz(timezone).format();
}

function createWaypointItineraryObject(itineraryItem, type, isEndpoint, tripData) {
    if (type !== 'depart' && type !== 'arrive') {
        throw new Error('type must be either \'depart\' or \'arrive\'');
    }

    const selfTransfer = itineraryItem.travel_type === 'self_transfer';
    const selfTransferType = (type === 'depart'
        ? 'arrive'
        : 'depart');
    const srcType = selfTransfer
        ? selfTransferType
        : type;
    const sourceDateTime = itineraryItem[`${srcType}_datetime`];
    const sourceTimezone = itineraryItem[`${srcType}_timezone`] || itineraryItem.location_timezone;
    const sourceState = itineraryItem[`${srcType}_state`] || itineraryItem.location_state;
    const sourceCountry = itineraryItem[`${srcType}_country`] || itineraryItem.location_country;
    const sourceCityName = itineraryItem[`${srcType}_cityname`] || itineraryItem.location_cityname;
    const sourceName = itineraryItem[`${srcType}_name`] || itineraryItem.location_name;

    const arrivalStationId = itineraryItem.arrive_id || itineraryItem.location_id;
    const departureStationId = itineraryItem.depart_id || itineraryItem.location_id;

    return {
        arrivalStationId,
        carrierId: itineraryItem.carrier_id,
        city: sourceCityName,
        departureStationId,
        name: sourceName,
        operatorName: itineraryItem.operator_name,
        partId: `waypoint-${type}-${itineraryItem.part_id}`,
        region: regionFromStateAndCountry(sourceState, sourceCountry),
        timestamp: buildLocalizedTime({
            dateTime: sourceDateTime,
            timezone: sourceTimezone,
        }),
        tripId: tripData.trip_id,
        type: 'waypoint',
        waypointType: type,
    };
}

function createNonTravelItineraryObject(itineraryItem) {
    return {
        arriveTimestamp: buildLocalizedTime({
            dateTime: itineraryItem.arrive_datetime,
            timezone: itineraryItem.arrive_timezone || itineraryItem.location_timezone,
        }),
        departTimestamp: buildLocalizedTime({
            dateTime: itineraryItem.depart_datetime,
            timezone: itineraryItem.depart_timezone || itineraryItem.location_timezone,
        }),
        partId: `${itineraryItem.stop_type}-${itineraryItem.part_id}`,
        type: itineraryItem.travel_type || itineraryItem.stop_type,
    };
}

function createSelfTransferItineraryObject(itineraryItem) {
    return {
        arriveStationId: itineraryItem.arrive_id,
        arriveTimestamp: buildLocalizedTime({
            dateTime: itineraryItem.arrive_datetime,
            timezone: itineraryItem.arrive_timezone,
        }),
        departStationId: itineraryItem.depart_id,
        departTimestamp: buildLocalizedTime({
            dateTime: itineraryItem.depart_datetime,
            timezone: itineraryItem.arrive_timezone,
        }),
        partId: `${itineraryItem.part_id}-self-transfer`,
        type: 'selfTransfer',
    };
}

function createStopItineraryObject(itineraryItem) {
    return {
        city: itineraryItem.arrive_cityname,
        name: itineraryItem.arrive_name,
        partId: `stop-${itineraryItem.part_id}`,
        region: regionFromStateAndCountry(itineraryItem.arrive_state, itineraryItem.arrive_country),
        timestamp: buildLocalizedTime({
            dateTime: itineraryItem.arrive_datetime,
            timezone: itineraryItem.arrive_timezone,
        }),
    };
}

function createStopListItineraryObject(itineraryItems) {
    const result = {
        stops: [],
        type: 'stopList',
    };

    if (itineraryItems.length === 0) {
        return result;
    }

    /* make sure to filter out the layover -> stop conversions from the list of stops we're building
     * here.  a layover is always preceded in the stop list by a scheduled-travel object, and that
     * scheduled-travel object will by definition have the same destination as the layover.  not
     * filtering out the layover here will result in duplicate stops being shown in the stop list */

    /* uh but also the first item in the stop list can be a layover too, which (since it's the first
     * item) isn't preceded by anything.  make sure we handle that too */
    if (itineraryItems[0].stop_type === 'layover') {
        result.stops.push(createStopItineraryObject(itineraryItems[0]));
    }

    return itineraryItems
        .filter((item) => item.stop_type !== 'layover')
        .reduce((acc, item) => {
            return {
                ...acc,
                stops: [
                    ...acc.stops,
                    createStopItineraryObject(item),
                ],
            };
        }, result);
}

/**
 * @param {Array<Object>} rawItinerary
 * @param {Array<Object>} tripLegs
 * @return {Array<Object>} something a little easier for React to render
 */
function calculateItinerary(rawItinerary, tripLegs) {
    if (!rawItinerary) {
        return undefined;
    }

    /* one iteration of `reduce` might result in >1 items being consumed.  we need a counter outside
     * of the loop to track where we are */
    let nextUnhandledItemIndex = -1;

    return rawItinerary.reduce((result, item, rIdx) => {
        /* this object has been consumed already (see below) and we shouldn't process it here */
        if (rIdx < nextUnhandledItemIndex) {
            return result;
        }

        /* non-travel objects (layovers, xfers) are first-class items in a trip itinerary */
        if (item.part_type !== 'travel') {
            return [
                ...result,
                createNonTravelItineraryObject(item),
            ];
        }

        if (item.travel_type === 'self_transfer') {
            return [
                ...result,
                createSelfTransferItineraryObject(item),
            ];
        }

        /* this is a scheduled-travel object, which means we now add the following:
         * - a depart waypoint built from this itinerary object;
         * - a (maybe-zero-length) list of stops built from subsequent travel objects; and
         * - an arrive waypoint built from the final travel object
         */
        const departureWaypoint = createWaypointItineraryObject(item, 'depart', rIdx === 0, tripLegs);

        /* depart waypoints might be following an overnight layover or transfer */
        if (rIdx > 0) {
            const lastArriveTime = result[result.length - 1].arriveTimestamp
                || result[result.length - 1].timestamp;

            departureWaypoint.showArrivalTime = arrivesNextDay(
                lastArriveTime,
                departureWaypoint.timestamp,
            );
        }

        /* roll up all stops (and really short layovers) and turn them into a single stop list */
        let noMoreStopsIndex = rIdx;

        while (noMoreStopsIndex < rawItinerary.length) {
            const {
                arrive_datetime: arriveDatetime,
                depart_datetime: departDatetime,
                part_type: partType,
                stop_type: stopType,
                travel_type: travelType,
            } = rawItinerary[noMoreStopsIndex];

            if (stopType === 'layover'
                && departDatetime - arriveDatetime <= LAYOVER_TO_STOP_THRESHOLD) {
                // a short layover
                noMoreStopsIndex += 1;
            } else if (partType !== 'travel' || travelType !== 'scheduled') {
                // a transfer, a self-transfer, or a long-enough layover
                break;
            } else {
                // a travel object which will be turned into a stop
                noMoreStopsIndex += 1;
            }
        }

        /* we're about to consume everything in the raw itinerary up to here */
        nextUnhandledItemIndex = noMoreStopsIndex;

        const stopList = createStopListItineraryObject(
            rawItinerary.slice(rIdx, noMoreStopsIndex - 1),
        );
        /* a stop list might be of length 0, which means we can't calculate some things inside of
         * createStopListItineraryObject() because it won't have the data */

        /* but maybe we reached the end of the itinerary while looking for stops, so watch out for
         * fencepost errors */
        const isLast = nextUnhandledItemIndex === rawItinerary.length;
        const arrivalItineraryObject = rawItinerary[isLast
            ? nextUnhandledItemIndex - 1
            : nextUnhandledItemIndex];
        const stopListArriveTime = arrivalItineraryObject.arrive_datetime;
        stopList.duration = (stopListArriveTime - item.depart_datetime) * 1000;
        stopList.vehicleType = item.vehicle_type;
        stopList.partId = `${item.part_id}-${arrivalItineraryObject.part_id}`;

        const arrivalWaypoint = createWaypointItineraryObject(arrivalItineraryObject, 'arrive', isLast, tripLegs);

        arrivalWaypoint.showArrivalTime = arrivesNextDay(
            departureWaypoint.timestamp,
            arrivalWaypoint.timestamp,
        );

        return [
            ...result,
            departureWaypoint,
            stopList,
            arrivalWaypoint,
        ];
    }, []);
}

/**
 * @param {Object} promotions - promo code data
 * @param {Boolean} isMultiLeggedTrip
 * @return {Array} promotions applied to this trip
 */
function getPromotions(promotions, isMultiLeggedTrip) {
    if (!promotions) {
        return [];
    }

    const {
        applied = [],
        auto_enabled: autoEnabled = [],
        definitions = {},
    } = promotions;

    function idToPromotionObject(id) {
        return {
            discount: definitions[id].discount,
            generic: false,
            id: definitions[id].promo_id,
        };
    }

    if (applied.length) {
        if (isMultiLeggedTrip) {
            return [{
                discount: {},
                generic: true,
                id: 'generic',
            }];
        }

        return applied.map(idToPromotionObject);
    }

    if (autoEnabled.length) {
        if (isMultiLeggedTrip) {
            return [{
                discount: {},
                generic: true,
                id: 'generic',
            }];
        }

        return autoEnabled.map(idToPromotionObject);
    }

    return [];
}

/**
 * each leg of a routed trip has its own array of travel modes (bus/train/ferry),
 * so we can potentially run into cases where one leg is [ `bus`, `train` ] and
 * the second leg is [ `ferry`, `bus` ].  how should that be displayed?  there's
 * not really a great way to answer that, especially since I'm told that the
 * order in which travel modes appear for a search result (or for each leg of a
 * routed search result) are in no particular order.
 *
 * @param {Array<Object>} tripLegs
 */
function getRoutedTripModes(tripLegs) {
    // preserve order of reported modes on a per-leg basis; it's the best we can do
    const routedModes = tripLegs.reduce((modes, leg) => {
        /* if routed trip, we've already mapped the child legs, so use the mapped field
         * name here */
        leg.tripModes.forEach((mode) => {
            modes.add(mode);
        });

        return modes;
    }, new Set());

    return [
        ...routedModes,
    ];
}

function formatTripPricing(pricing) {
    if (!pricing) {
        return undefined;
    }

    return Object.entries(pricing).reduce((priceMap, [currency, currencyMap]) => ({
        ...priceMap,
        [currency]: currencyMap.price,
    }), {});
}
/**
 * the pricing field of a routed trip should be a rolled-up version of the pricing fields of all
 * legs of the trip
 *
 * @param {Object} pricingData
 * @return {Array<Object>} a rolled-up pricing object
 */
function getRoutedTripPricing(pricingData) {
    return pricingData.reduce((pricingArr, pricing) => {
        const formattedTripPrice = formatTripPricing(pricing);
        if (!formattedTripPrice) {
            return pricingArr;
        }

        return [
            ...pricingArr,
            formattedTripPrice,
        ];
    }, []);
}

/**
 * @param {Object} pricing
 * @return {Array<Object>} only the stuff we care about; empty when the trip
 * has no pricing (trip.pricing === null must not produce [undefined] —
 * selectTripPrices treats any non-empty array as real pricing and crashes
 * on Object.entries(undefined))
 */
function getSingleTripPricing(pricing) {
    const formattedTripPrice = formatTripPricing(pricing);
    return formattedTripPrice ? [formattedTripPrice] : [];
}

function formatTripFees(pricing) {
    if (!pricing) {
        return undefined;
    }

    const fees = Object.entries(pricing).reduce((acc, [currency, currencyMap]) => {
        if (!currencyMap?.fees) {
            return acc;
        }

        return {
            ...acc,
            [currency]: currencyMap.fees,
        };
    }, {});

    if (!Object.entries(fees)?.length) {
        return undefined;
    }

    return fees;
}

/**
 * the fees field of a routed trip should be a rolled-up version of the fees fields of all
 * legs of the trip
 *
 * @param {Object} pricingData
 * @return {Array<Object>} a rolled-up fees object
 */
function getRoutedTripFees(pricingData) {
    return pricingData.reduce((acc, pricing) => {
        const fees = formatTripFees(pricing);

        if (!fees) {
            return acc;
        }

        return [
            ...acc,
            fees,
        ];
    }, []);
}

/**
 * @param {Object} pricing
 * @return {Array<Object>} only the stuff we care about
 */
function getSingleTripFees(pricing) {
    const fees = formatTripFees(pricing);
    if (!fees) {
        return undefined;
    }

    return [fees];
}

function getRoutedDuration(trip) {
    const departDatetime = trip.triplegs[0].depart_datetime;
    const arriveDatetime = trip.triplegs[trip.triplegs.length - 1].arrive_datetime;

    return tripDuration(departDatetime, arriveDatetime);
}

function getSingleDuration(trip) {
    return tripDuration(trip.depart_datetime, trip.arrive_datetime);
}

/**
 * @param {Object} psearchResult
 * @return {Boolean}
 */
function isRoutedTrip(psearchResult) {
    return Array.isArray(psearchResult.triplegs);
}

/**
 * @param {String|Number} departDateTime
 * @param {String|Number} arriveDateTime
 * @return {Number} milliseconds
 */
function tripDuration(departDateTime, arriveDateTime) {
    return i18nMoment(arriveDateTime).diff(i18nMoment(departDateTime));
}

function calculatePricingInUSD(pricing) {
    return pricing.reduce((total, pricingObj) => {
        const [currency, price] = Object.entries(pricingObj)[0];
        return total + i18nMoney(price, currency)
            .setCurrency('USD')
            .getConvertedPrice();
    }, 0);
}

function buildFakeItineraryLeg(leg, tripCarrierData) {
    return {
        arrive_cityname: leg.arrive_cityname,
        arrive_country: leg.arrive_country,
        arrive_datetime: leg.arrive_datetime_utc,
        arrive_id: leg.arrive_id,
        arrive_name: leg.arrive_name,
        arrive_state: leg.arrive_state,
        arrive_timezone:
            `Etc/GMT${leg.arrive_timezone_offset < 0
                ? '+'
                : '-'}${Math.abs(leg.arrive_timezone_offset)}`,
        carrier_id: leg.carrier,
        depart_cityname: leg.depart_cityname,
        depart_country: leg.depart_country,
        depart_datetime: leg.depart_datetime_utc,
        depart_id: leg.depart_id,
        depart_name: leg.depart_name,
        depart_state: leg.depart_state,
        depart_timezone:
            `Etc/GMT${leg.depart_timezone_offset < 0
                ? '+'
                : '-'}${Math.abs(leg.depart_timezone_offset)}`,
        operator_name: tripCarrierData.name,
        part_id: `${leg.depart_datetime_utc}-${leg.arrive_datetime_utc}`,
        part_type: 'travel',
        travel_type: 'scheduled',
        vehicle_type: leg.vehicle_types
            ? leg.vehicle_types[0]
            : null,
    };
}

/**
 * make a fake itinerary object for trips which don't support it
 * @param {Object} trip
 * @param {Object} tripCarrierData
 * @return {Object} a fake itinerary-info object
 */
function buildFakeItinerary(trip, tripCarrierData) {
    if (!trip || !tripCarrierData) {
        return undefined;
    }

    const {
        supplementalInfo,
    } = tripCarrierData;
    const complete = !supplementalInfo || !supplementalInfo.itineraryinfo;

    return {
        itinerary: [buildFakeItineraryLeg(trip, tripCarrierData)],
        metadata: {
            complete,
            tripId: trip.trip_id,
        },
    };
}

const flattenTripModes = (tripModes) => tripModes.reduce((acc, cur) => acc.concat(cur), []);

/**
 * @return {Object} mapped accessibility info object
 */
function getAccessibilityData(accessibility = {}) {
    const {
        max_wheelchair_seats: maxWheelchairSeats = 0,
    } = accessibility;

    return {
        maxWheelchairSeats,
    };
}

export {
    arrivesNextDay,
    buildEndpointData,
    buildFakeItinerary,
    buildLocalizedTime,
    calculateItinerary,
    calculatePricingInUSD,
    flattenTripModes,
    getAccessibilityData,
    getNumberOfMidnightsPassed,
    getPromotions,
    getRoutedDuration,
    getRoutedTripFees,
    getRoutedTripModes,
    getRoutedTripPricing,
    getSingleDuration,
    getSingleTripFees,
    getSingleTripPricing,
    isRoutedTrip,
    tripDuration,
};
